import React from 'react';
import PrimaryButton from './Components/Buttons/PrimaryButton';
import OutlinedPrimaryButton from './Components/Buttons/OutlinedPrimaryButton';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      isReloading: false,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Auto-reload on ChunkLoadError (happens when deploying new versions)
    // This occurs when cached JS tries to load chunks that no longer exist
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk') || error?.message?.includes('Loading CSS chunk')) {
      // Check if we haven't already tried reloading recently (prevent infinite loops)
      const lastReload = sessionStorage.getItem('chunkErrorReload');
      const now = Date.now();

      if (!lastReload || (now - parseInt(lastReload, 10)) > 10000) {
        // Set flag and reload after a short delay
        sessionStorage.setItem('chunkErrorReload', now.toString());
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    }
  }

  handleReload = () => {
    this.setState({ isReloading: true });
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className='d-flex justify-content-center align-items-center vh-100' style={{ background: 'var(--lighter)' }}>
          <style>{`
            @keyframes fadeInUp {
              from { opacity: 0; transform: translateY(30px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.05); }
              100% { transform: scale(1); }
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes slideIn {
              from { opacity: 0; max-height: 0; }
              to { opacity: 1; max-height: 200px; }
            }
            .error-container {
              animation: fadeInUp 0.6s ease-out;
            }
            .error-icon {
              animation: pulse 2s ease-in-out infinite;
            }
            .reload-spinner {
              animation: spin 1s linear infinite;
            }
            .error-details {
              animation: slideIn 0.3s ease-out;
              overflow: hidden;
              transition: all 0.3s ease;
            }
          `}</style>
          <div
            className='error-container card p-5 shadow-lg text-center'
            style={{
              borderRadius: '16px',
              border: 'none',
              background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95))',
              backdropFilter: 'blur(20px)',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 25px rgba(0, 0, 0, 0.08)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Animated background gradient */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '200%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(118, 56, 177, 0.03), transparent)',
                animation: 'slideIn 3s ease-in-out infinite'
              }}
            />

            {/* Error Icon */}
            <div
              className='error-icon mb-4 mx-auto'
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #FF6B6B 0%, #EE5A52 50%, #D63031 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '32px',
                boxShadow: '0 8px 20px rgba(214, 48, 49, 0.3)',
                position: 'relative',
                zIndex: 2
              }}
            >
              {this.state.isReloading ? (
                <div className="reload-spinner">↻</div>
              ) : (
                '⚠'
              )}
            </div>

            {/* Error Title */}
            <h2
              style={{
                fontFamily: 'var(--font-primary)',
                fontWeight: 600,
                color: 'var(--primary-darkest)',
                marginBottom: '0.75rem',
                fontSize: '1.5rem',
                position: 'relative',
                zIndex: 2
              }}
            >
              Oops! Something went wrong
            </h2>

            {/* Error Description */}
            <p
              style={{
                color: '#64748B',
                marginBottom: '2rem',
                fontFamily: 'var(--font-secondary)',
                fontSize: '1rem',
                lineHeight: '1.6',
                maxWidth: '400px',
                margin: '0 auto 2rem auto',
                position: 'relative',
                zIndex: 2
              }}
            >
              We encountered an unexpected error that prevented the application from working properly.
              Don't worry - this is usually temporary.
            </p>

            {/* Action Buttons */}
            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center mb-3">
              <PrimaryButton
                onClick={this.handleReload}
                disabled={this.state.isReloading}
                padding="0.75rem 1.5rem"
                fontSize="0.95rem"
                borderRadius="10px"
                boxShadow="0 4px 15px rgba(118, 56, 177, 0.3)"
                style={{
                  position: 'relative',
                  zIndex: 2,
                  transform: this.state.isReloading ? 'scale(0.98)' : 'scale(1)'
                }}
              >
                {this.state.isReloading ? (
                  <>
                    <div className="reload-spinner">↻</div>
                    Reloading...
                  </>
                ) : (
                  <>
                    ↻ Reload Application
                  </>
                )}
              </PrimaryButton>

              <OutlinedPrimaryButton
                onClick={this.toggleDetails}
                padding="0.75rem 1rem"
                fontSize="0.9rem"
                borderRadius="10px"
                style={{
                  position: 'relative',
                  zIndex: 2
                }}
              >
                {this.state.showDetails ? 'Hide' : 'Show'} Details
              </OutlinedPrimaryButton>
            </div>

            {/* Error Details (Collapsible) */}
            {this.state.showDetails && (
              <div
                className="error-details mt-3 p-3"
                style={{
                  background: 'rgba(248, 249, 250, 0.8)',
                  borderRadius: '8px',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  textAlign: 'left',
                  fontSize: '0.8rem',
                  fontFamily: 'monospace',
                  color: '#6B7280',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  position: 'relative',
                  zIndex: 2
                }}
              >
                <div className="mb-2">
                  <strong>Error:</strong> {this.state.error?.toString() || 'Unknown error'}
                </div>
                {this.state.errorInfo && (
                  <div>
                    <strong>Component Stack:</strong>
                    <pre style={{ fontSize: '0.75rem', marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div
              style={{
                marginTop: '1.5rem',
                fontSize: '0.85rem',
                color: '#9CA3AF',
                position: 'relative',
                zIndex: 2
              }}
            >
              If the problem persists, please contact support
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
